import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from '../../../config/axios';
import { Button } from '../../components/Atoms';
import AuthShell, { AuthLabel, AuthError, authInputStyle } from './AuthShell';

/**
 * Login w Terroir - zastępuje pages/Login.js.
 *
 * Używa istniejącego useAuth() z context/AuthContext (httpOnly cookie po
 * security-fixes z 23.04). Zachowuje zachowanie redirectu po success.
 */

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Komunikat po wymuszonym wylogowaniu (ban/suspend/wygaśnięcie sesji — A1/A4).
  const [error, setError] = useState(() => {
    try {
      const notice = sessionStorage.getItem('auth_notice');
      if (notice) { sessionStorage.removeItem('auth_notice'); return notice; }
    } catch { /* prywatny tryb */ }
    return '';
  });
  const [loading, setLoading] = useState(false);
  // UX1: gdy login zwróci EMAIL_NOT_VERIFIED, pokazujemy przycisk ponownej wysyłki
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerify(false);
    setResendState('idle');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      // Routing po loginie:
      // - admin → /admin
      // - bez ukończonego onboardingu → /onboarding (nowy user)
      // - pozostali → /dashboard
      const u = result.user || {};
      const isAdmin = u.role === 'admin' || u.username === 'admin';
      if (isAdmin) {
        navigate('/admin');
      } else if (!result.onboardingCompleted) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error || 'Błąd logowania');
      if (result.code === 'EMAIL_NOT_VERIFIED') setNeedsVerify(true);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendState('sending');
    try {
      await axios.post('/api/auth/resend-verification-public', { email });
    } catch { /* endpoint jest generyczny — ignorujemy błąd */ }
    setResendState('sent');
  };

  return (
    <AuthShell
      eyebrow="Zaloguj się"
      title={<>Witaj <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>z powrotem</span>.</>}
      subtitle="Twój ogród czekał. Zaloguj się, żeby zobaczyć co dziś robić."
      footer={
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10,
          fontSize: 13, color: 'var(--t-text-dim)',
        }}>
          <div>
            Nie masz konta?{' '}
            <Link to="/register" style={{
              color: 'var(--t-sage)', textDecoration: 'none', fontWeight: 500,
            }}>Zarejestruj się</Link>
          </div>
          <Link to="/forgot-password" style={{
            color: 'var(--t-text-mute)', textDecoration: 'none',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
          }}>Nie pamiętam hasła</Link>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AuthError>{error}</AuthError>

        {needsVerify && (
          <div style={{
            marginBottom: 16, padding: '12px 14px', borderRadius: 10,
            background: 'rgba(168, 196, 158, 0.08)',
            border: '1px solid rgba(168, 196, 158, 0.25)',
            fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5,
          }}>
            {resendState === 'sent' ? (
              <span>✓ Jeśli konto wymaga weryfikacji, wysłaliśmy nowy link. Sprawdź skrzynkę (i spam).</span>
            ) : (
              <>
                Konto nie zostało jeszcze zweryfikowane.{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendState === 'sending' || !email}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'var(--t-sage)', cursor: 'pointer', fontWeight: 500,
                    fontFamily: 'var(--t-sans)', fontSize: 13, textDecoration: 'underline',
                  }}
                >{resendState === 'sending' ? 'Wysyłanie…' : 'Wyślij link weryfikacyjny ponownie'}</button>
              </>
            )}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="twoj@email.com"
            style={authInputStyle}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <AuthLabel htmlFor="password">Hasło</AuthLabel>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            style={authInputStyle}
          />
        </div>

        <Button
          type="submit"
          full
          variant={loading || !email || !password ? 'ghost' : 'primary'}
        >{loading ? 'Logowanie…' : 'Zaloguj się'}</Button>
      </form>
    </AuthShell>
  );
}
